import React, { useContext, useReducer } from 'react';
import Grid from '@mui/material/Grid';
import { Button, Card, CardContent } from '@mui/material';
import dayjs from 'dayjs';
import { createCoupon } from 'features/coupon/api/coupons';
import { modelMap } from 'shared/utils';
import { VARIANTS } from 'shared/constants';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { Title } from 'shared/components/abstract/interactables';
import { getCouponPath } from 'shared/routes/coupon';
import CouponInfo from './CouponInfo';
import CouponForm from './CouponForm';
import {
  couponTypeEnums,
  discountDurationEnums,
  discountTypeEnums,
  numberOfRedemptionEnums,
  redemptionCodeEnums,
} from './helper';

const tomorrow = dayjs()
  .add(1, 'day');

const initialCouponCreatorState = {
  couponType: '',
  lastCreatedId: '',
  refinerKey: 'manager',
  selectedTenant: {},
  refinerResults: {
    complex: [],
    manager: [],
    property: [],
  },
  couponFormState: {
    amountOff: 1000,
    couponId: '',
    description: '',
    discountType: discountTypeEnums.percentOff,
    duration: discountDurationEnums.repeating,
    durationInMonths: '6',
    idType: redemptionCodeEnums.auto,
    maxRedemption: '1',
    percentOff: '25',
    redeemBy: tomorrow,
    redemptionType: numberOfRedemptionEnums.unlimited,
    timesRedeemed: 0,
  },
};

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case 'coupon':
      return {
        ...initialCouponCreatorState,
        lastCreatedId: payload.id,
      };

    case 'tenant':
      return {
        ...state,
        selectedTenant: payload && payload.id ? payload : {},
      };

    case 'couponType': {
      const { couponFormState: { ...couponFormState } } = state;
      if (payload === couponTypeEnums.tenant) {
        couponFormState.maxRedemption = '1';
        couponFormState.redemptionType = redemptionCodeEnums.custom;
        couponFormState.duration = discountDurationEnums.once;
      } else {
        couponFormState.maxRedemption = '100';
        couponFormState.redemptionType = numberOfRedemptionEnums.unlimited;
        couponFormState.duration = discountDurationEnums.repeating;
        couponFormState.durationInMonths = '6';
      }

      return {
        ...state,
        couponFormState,
        couponType: payload || '',
      };
    }

    case 'price': {
      const { couponFormState: { ...couponFormState } } = state;
      couponFormState.amountOff = payload;

      return {
        ...state,
        couponFormState,
      };
    }

    case 'percentage': {
      const { couponFormState: { ...couponFormState } } = state;
      couponFormState.percentOff = payload;

      return {
        ...state,
        couponFormState,
      };
    }

    case 'refiner': {
      const {
        results: newResults,
        key: newKey,
      } = payload;
      if (!newResults && !newKey) {
        return {
          ...state,
          refinerResults: {
            complex: [],
            manager: [],
            property: [],
          },
        };
      }

      const { refinerResults: { ...refinerResults } } = state;
      // key should be smallest unit
      let key = newKey || state.refinerKey;

      refinerResults[key] = newResults;

      if (refinerResults.manager.length > 0) {
        key = 'manager';
      }
      if (refinerResults.complex.length > 0) {
        key = 'complex';
      }
      if (refinerResults.property.length > 0) {
        key = 'property';
      }

      return {
        ...state,
        refinerResults,
        refinerKey: key,
      };
    }

    case 'redeemBy': {
      const { couponFormState: { ...couponFormState } } = state;
      couponFormState.redeemBy = payload;
      return {
        ...state,
        couponFormState,
      };
    }

    case 'couponFormState': {
      const {
        name,
        value,
      } = payload;
      const { couponFormState: { ...couponFormState } } = state;
      couponFormState[name] = value;
      return {
        ...state,
        couponFormState,
      };
    }

    default:
      return state;
  }
}

export default function CouponCreator() {
  const [state, dispatch] = useReducer(reducer, initialCouponCreatorState);

  const {
    couponFormState,
    couponType,
    refinerKey,
    refinerResults,
    selectedTenant,
    couponFormState: {
      amountOff,
      couponId,
      description,
      discountType,
      duration,
      durationInMonths,
      idType,
      maxRedemption,
      percentOff,
      redeemBy,
      redemptionType,
    },
  } = state;

  const {
    request,
    isLoading,
  } = useRequest();
  const notification = useNotification();
  const { user } = useAuth();
  const syncSideNav = useContext(SideNavContext);

  const submitCoupon = async (e) => {
    e.preventDefault();

    const params = {
      duration: duration.toLowerCase(),
      // Add parameters optionally to the request if their boolean statements are true
      ...(discountType === discountTypeEnums.amountOff && { amountOff }),
      ...(discountType === discountTypeEnums.percentOff
          && { percentOff: parseInt(percentOff, 10) }),
      ...(idType === redemptionCodeEnums.custom && { id: couponId }),
      ...(redemptionType === numberOfRedemptionEnums.custom
          && { maxRedemption: parseInt(maxRedemption, 10) }),
      ...(duration === discountDurationEnums.repeating
          && { durationInMonths: parseInt(durationInMonths, 10) }),
      ...(redeemBy && { redeemBy: redeemBy.unix() }),
    };

    let rules;
    let ruleType;
    if (couponType === couponTypeEnums.tenant) {
      if (!selectedTenant.id) {
        notification.pushError('Please select a customer');
        return;
      }

      rules = [{ tenantId: selectedTenant.id }];
      ruleType = 'tenant';
    } else {
      ruleType = refinerKey;
      rules = refinerResults[refinerKey]
        ?.map((rule) => ({ [modelMap[ruleType].idKey]: rule.id }));
    }

    if (discountType === discountTypeEnums.amountOff && amountOff < 100) {
      notification.pushError('Discount amount must be at least one dollar');
      return;
    }

    if (discountType === discountTypeEnums.percentOff && percentOff < 1) {
      notification.pushError('Discount percentage must be at least 1%');
      return;
    }

    const result = await request(createCoupon(params, rules, ruleType, user.username, description));
    if (!result) {
      return;
    }

    notification.pushSuccess('Coupon Created');
    notification.pushInfo(`New Coupon ${result.id} Added to History`);
    syncSideNav(getCouponPath(result.id), result.id);
    dispatch({
      action: 'coupon',
      payload: result,
    });
  };

  const selectedRules = refinerResults[refinerKey];

  return (
    <Grid>
      <Title title="Create Coupons" />
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Fields on a card; the preview beside them is already one. */}
        <Grid
          size={{
            xs: 12,
            md: 12,
            lg: 8,
          }}
        >
          <Card>
            <CardContent>
              <Grid container>
                <CouponForm
                  couponFormState={couponFormState}
                  couponType={couponType}
                  handleChange={(name) => (value) => dispatch({
                    action: 'couponFormState',
                    payload: {
                      name,
                      value,
                    },
                  })}
                  handleCouponTypeChange={(value) => dispatch({
                    action: 'couponType',
                    payload: value,
                  })}
                  handleRedeemByChange={(value) => dispatch({
                    action: 'redeemBy',
                    payload: value,
                  })}
                  minDate={tomorrow}
                  numResultsSelected={selectedRules?.length || 0}
                  refinerKey={refinerKey}
                  refinerResults={refinerResults}
                  selectedTenant={selectedTenant}
                  setPrice={(newPrice) => dispatch({
                    action: 'price',
                    payload: newPrice,
                  })}
                  setPercentage={(newPercentage) => dispatch({
                    action: 'percentage',
                    payload: newPercentage,
                  })}
                  setRefiner={(results, key) => dispatch({
                    action: 'refiner',
                    payload: {
                      results,
                      key,
                    },
                  })}
                  setTenant={(newTenant) => dispatch({
                    action: 'tenant',
                    payload: newTenant,
                  })}
                  setValue={({
                    name,
                    value,
                  }) => dispatch({
                    action: 'couponFormState',
                    payload: {
                      name,
                      value,
                    },
                  })}
                  submitCoupon={submitCoupon}
                />
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          container
          size={{
            xs: 12,
            md: 12,
            lg: 4,
          }}
        >
          <CouponInfo
            coupon={couponFormState}
            couponId={couponFormState.couponId}
            couponRules={selectedRules}
            ruleType={couponType === couponTypeEnums.tenant ? 'tenant' : refinerKey}
            tenantId={selectedTenant.id}
            tenantName={`${selectedTenant.first_name} ${selectedTenant.last_name}`}
          >
            <Button
              disabled={isLoading || !couponType}
              form="coupon-form"
              type="submit"
              variant="contained"
              color={VARIANTS.primary}
            >
              Create Coupon
            </Button>
          </CouponInfo>
        </Grid>
      </Grid>
    </Grid>
  );
}
